import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncData } from '@myhealth-raccoon/services';
import { Button, Spinner } from '@myhealth-raccoon/components';
import { useBeforeUnload } from 'react-use';

import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { useSelector } from 'src/store';
import { patientService } from 'src/services/patient';
import { UpdateEmail } from 'src/components/additionalDetails/UpdateEmail';
import { UpdatePHI } from 'src/components/additionalDetails/UpdatePHI';
import { PreventBackButton } from 'src/components/functional';
import { toast } from "react-toastify";
import { StickyBottomBar } from 'src/components/StickyBottomBar';
import { ContentContainer } from 'src/components/ContentContainer';
import { ConsentModule, ConsentModuleRef } from 'src/components/additionalDetails/ConsentModule';

export function AdditionalDetails() {
    useBookingFlowStep(3);
    const navigate = useNavigate();
    // show dialog before closing/reloading
    useBeforeUnload();

    const [isEmailDirty, setIsEmailDirty] = useState(false);
    const [isPhiDirty, setIsPhiDirty] = useState(false);
    const [isConsentDirty, setIsConsentDirty] = useState(false);
    const [shouldShowValidation, setShouldShowValidation] = useState(false);
    const consentModuleRef = useRef<ConsentModuleRef>(null);

    const siteId = useSelector(state => state.appointment.clinicId);

    const { data: healthInfo, isLoading: loadingHealthInfo, invoke: reloadHealthInfo } = useAsyncData({
        method: patientService.GetPatientHealthInfo,
        service: patientService
    }, siteId);

    const { data: basicDetails, isLoading: loadingBasicDetails, invoke: reloadBasicDetails, ...basicDetailsRequest } = useAsyncData({
        method: patientService.GetBasicDetails,
        service: patientService
    }, siteId);

    const onNext = useCallback(async () => {
        if (isEmailDirty || isPhiDirty || isConsentDirty) {
            toast.error("You have unsaved changes. Please cancel or complete pending updates.");
            setShouldShowValidation(true);
        } else {
            consentModuleRef.current?.submit();
            navigate('/app/consent')
        }
    }, [isConsentDirty, isEmailDirty, isPhiDirty, navigate]);

    return <>
        <ContentContainer className='flex-1 lg:flex-none'>
            <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Additional Info</h1>
            <p className='text-base'>Please review and update the details below if there have been any changes in the past 12 months.</p>
            <p className='text-base'><br /></p>
            <p className='text-base'><span className='text-primary'>*</span> indicates a required field</p>

            <h2 className='text-base font-semibold mt-8 mb-1'>
                Contact Email <span aria-label='Required' className='text-primary'>*</span>
            </h2>
            <p>
                We'll send your booking confirmation here.
            </p>

            {loadingBasicDetails
                ? <Spinner />
                : basicDetails ?
                    <UpdateEmail
                        className='my-5'
                        currentEmail={basicDetails.email}
                        setIsDirty={setIsEmailDirty}
                        afterSubmit={reloadBasicDetails}
                    />
                    : <pre className='text-error'>{basicDetailsRequest.error.message}</pre>}

            <hr className='my-5' />

            <h2 className='text-base font-semibold mt-5 mb-1'>
                Private Health Insurance
            </h2>

            <p>
                Your Medical Practice may partner with Health Insurance Companies to augment your care, giving you access to free or discounted services.
                <br />
                <br />
                All information is held in line with our <a target="_blank" href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}/privacy-policy/`} className='text-primary'>Privacy Policy</a>.
            </p>

            {loadingHealthInfo
                ? <Spinner />
                : <UpdatePHI
                    patientHealthInfo={healthInfo}
                    reloadHealthInfo={reloadHealthInfo}
                    className='my-5'
                    setIsDirty={setIsPhiDirty}
                />
            }
            <ConsentModule
                prepend={<>
                    <hr className='my-5' />
                    <h2 className='text-base font-semibold mt-5'>Your Communication Choices</h2>
                    <p className='mb-5'>
                        We may send you updates regarding health information.
                    </p>
                </>}
                setIsDirty={setIsConsentDirty}
                shouldShowValidation={shouldShowValidation}
                ref={consentModuleRef}
            />
        </ContentContainer>
        <StickyBottomBar>
            <Button className='flex-1 text-center' onClick={onNext}>Next</Button>
        </StickyBottomBar>
        <PreventBackButton />

    </>
}